import { Briefcase } from "lucide-react";

type Role = {
  company: string;
  title: string;
  period: string;
  location?: string;
  bullets: string[];
  stack?: string[];
};

const roles: Role[] = [
  {
    company: "Rishabh Software",
    title: "Technical Lead",
    period: "Jan 2023 — Present",
    location: "Ahmedabad, Gujarat, India",
    bullets: [
      "Lead cross-functional engineering teams delivering enterprise .NET & Angular solutions across multiple geographies.",
      "Own technical architecture for microservices, cloud patterns, and CI/CD across flagship client engagements.",
      "Drive AI adoption across the SDLC — prompt engineering, code generation, automated review and documentation.",
      "Partner with Product Owners to translate business outcomes into roadmaps, estimates, and shippable increments.",
    ],
    stack: [".NET Core", "Angular", "Microservices", "AWS", "Azure", "AI / Prompt Engineering"],
  },
  {
    company: "Rishabh Software",
    title: "Senior Software Engineer",
    period: "Feb 2022 — Jun 2023",
    location: "Ahmedabad, India",
    bullets: [
      "Built custom desktop and web automation tools like web scraping, document processing, workflow optimization.",
      "Shipped multiple client modules in ASP.NET MVC, Web API and Angular.",
      "Owned end-to-end delivery from estimation through deployment and post-release support.",
    ],
    stack: ["C#", "ASP.NET MVC", "Web API", "Angular", "Web Scraping", "SQL Server", "Azure"],
  },
  {
    company: "Assure Care LLC",
    title: "Senior Software Engineer",
    period: "Aug 2019 — Jan 2022",
    location: "India",
    bullets: [
      "Developed and maintained robust web applications using ASP.NET MVC and ASP.NET Web API.",
      "Engineered secure, high-performance RESTful APIs for complex data integrations.",
      "Acted as a senior technical resource, driving code quality through peer reviews and mentorship.",
      "Worked directly with stakeholders to align technical deliverables with product goals.",
    ],
    stack: ["C#", "ASP.NET", "MVC", "JavaScript", "SQL Server", "Jquery"],
  },
  {
    company: "Earlier Roles",
    title: "Software Engineer",
    period: "2013 — 2015",
    location: "India",
    bullets: [
      "Started career as a .NET developer building line-of-business web applications.",
      "Grew through hands-on work across the full stack — backend services, databases and frontend.",
    ],
    stack: ["C#", "ASP.NET", "JavaScript", "SQL Server", "Windows Forms", "WPF"],
  },
];

export function Experience() {
  return (
    <section id="experience" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 max-w-2xl">
          <p className="font-mono text-sm text-primary">// Experience</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            12+ years building, leading, shipping.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            A timeline of the roles where I&apos;ve grown from hands-on .NET engineer into a technical lead owning architecture, delivery, and team performance.
          </p>
        </div>

        <ol className="relative space-y-10 border-l border-border pl-6 md:space-y-12 md:pl-10">
          {roles.map((role, idx) => (
            <li key={`${role.company}-${idx}`} className="relative">
              <span
                aria-hidden
                className="absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border border-primary/40 bg-background text-primary shadow-card md:-left-[35px] md:h-8 md:w-8"
              >
                <Briefcase className="h-3 w-3 md:h-4 md:w-4" />
              </span>

              <div className="group rounded-xl border border-border bg-surface p-6 transition-smooth hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-elevated md:p-8">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <h3 className="text-lg font-semibold text-foreground md:text-xl">
                    {role.title}
                    <span className="text-muted-foreground"> · </span>
                    <span className="text-primary">{role.company}</span>
                  </h3>
                  <span className="font-mono text-xs text-muted-foreground md:text-sm">
                    {role.period}
                  </span>
                </div>

                {role.location && (
                  <p className="mt-1 text-sm text-muted-foreground">{role.location}</p>
                )}

                <ul className="mt-4 space-y-2 text-sm text-muted-foreground leading-relaxed md:text-base">
                  {role.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {role.stack && role.stack.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {role.stack.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-border bg-background/50 px-2.5 py-1 font-mono text-xs text-muted-foreground transition-smooth group-hover:border-primary/30 group-hover:text-foreground"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
